import React, { createContext, useContext, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { DataProvider } from './types';

export interface HopChartsContextValue {
  provider: DataProvider;
  queryClient: QueryClient;
}

const HopChartsContext = createContext<HopChartsContextValue | null>(null);

export interface HopChartsProviderProps {
  provider: DataProvider;
  /**
   * Bring your own react-query client (e.g. the host app's). When omitted, a
   * nested HopChartsProvider reuses its parent's client and a top-level one
   * creates its own.
   */
  queryClient?: QueryClient;
  children: React.ReactNode;
}

function defaultQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { refetchOnWindowFocus: false, retry: 1 },
    },
  });
}

/**
 * Supplies the data provider (and a react-query client) to every HopChart
 * below it. Mount once near the root of your app, or let HopChart create one
 * for itself by passing it a `provider` prop.
 */
export function HopChartsProvider({ provider, queryClient, children }: HopChartsProviderProps) {
  const parent = useContext(HopChartsContext);
  const client = useMemo(
    () => queryClient ?? parent?.queryClient ?? defaultQueryClient(),
    [queryClient, parent?.queryClient],
  );
  const value = useMemo<HopChartsContextValue>(() => ({ provider, queryClient: client }), [provider, client]);

  return (
    <HopChartsContext.Provider value={value}>
      <QueryClientProvider client={client}>{children}</QueryClientProvider>
    </HopChartsContext.Provider>
  );
}

/** The nearest HopChartsProvider's value, or null outside one. */
export function useHopChartsContext(): HopChartsContextValue | null {
  return useContext(HopChartsContext);
}

/** The active data provider. Throws when rendered outside a HopChartsProvider. */
export function useDataProvider(): DataProvider {
  const ctx = useContext(HopChartsContext);
  if (!ctx) {
    throw new Error(
      'hopcharts: no data provider. Wrap your tree in <HopChartsProvider provider={...}> or pass `provider` to <HopChart>.',
    );
  }
  return ctx.provider;
}
